import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';
import iconUrl from '../../assets/icon.svg';

/** Public landing page — the app's home page at `/`, rendered standalone (no app
 *  sidebar). Also the "App homepage" URL for Google's OAuth consent screen. Text
 *  follows the selected language (i18n) with a language switcher; describes what
 *  the app does and how it uses Google Calendar, and links to sign in, the
 *  Privacy Policy and Terms. */
export default function LandingPage() {
  const { t } = useTranslation();
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <div className="mx-auto max-w-3xl px-4 py-14 sm:py-20">
        {/* Logo (top left) · language switcher (right). */}
        <div className="flex items-center justify-between gap-4">
          <img src={iconUrl} alt={t('landing.brand')} className="h-10 w-10 shrink-0" />
          <LanguageSwitcher />
        </div>

        {/* Hero — the app name is the PRIMARY heading (Google verification wants
            it as the clear title/header), the tagline sits beneath it. */}
        <header className="mt-10 space-y-3">
          <h1 className="text-3xl font-semibold leading-tight sm:text-4xl">{t('landing.brand')}</h1>
          <p className="text-lg text-muted-foreground">{t('landing.title')}</p>
        </header>

        <div className="mt-8">
          <Link to="/login" className="btn-primary inline-flex">
            {t('landing.signIn')}
          </Link>
        </div>

        {/* Description */}
        <section className="mt-12 space-y-4 text-sm leading-relaxed">
          <h2 className="text-base font-semibold">{t('landing.aboutHeading')}</h2>
          <p className="text-muted-foreground">{t('landing.about1')}</p>
          <p className="text-muted-foreground">{t('landing.about2')}</p>
          <p className="text-muted-foreground">{t('landing.about3')}</p>
          <p className="text-muted-foreground">
            <Trans
              i18nKey="landing.privacyNote"
              components={{ a: <Link to="/privacy" className="text-primary hover:underline" /> }}
            />
          </p>
          <p>
            <Link to="/about" className="text-primary hover:underline">
              {t('landing.aboutLink')} →
            </Link>
          </p>
        </section>

        {/* Footer */}
        <footer className="mt-14 border-t border-border pt-6 text-xs text-muted-foreground">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <Link to="/about" className="hover:text-foreground hover:underline">
              {t('landing.aboutLink')}
            </Link>
            <span aria-hidden>·</span>
            <Link to="/privacy" className="hover:text-foreground hover:underline">
              {t('legal.privacy')}
            </Link>
            <span aria-hidden>·</span>
            <Link to="/terms" className="hover:text-foreground hover:underline">
              {t('legal.terms')}
            </Link>
          </div>
          <p className="mt-3">
            {t('landing.operatedBy')} —{' '}
            <a className="hover:text-foreground hover:underline" href="mailto:info@diadraw.com">
              info@diadraw.com
            </a>
          </p>
        </footer>
      </div>
    </div>
  );
}
